import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers } from '@/services/api/users';
import { getDomains } from '@/services/api/domains';
import {
    getUserDomainAccess,
    setUserDomainAccess,
    setUserRole,
    aprovarUsuario,
    desativarUsuario,
    type Papel,
} from '@/services/api/domain-access';
import { getCurrentUser } from '@/services/api/auth';
import NovoUsuarioModal from './NovoUsuarioModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
    RefreshCw, AlertCircle, Shield, Search, Save, X, Globe,
    UserPlus, Check, Ban, ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { User } from '@/types/user';
import type { Domain } from '@/types/domain';

/**
 * Usuários e Acessos — tela única de administração.
 *
 * Faz quatro coisas que antes não existiam ou estavam espalhadas:
 *   1. criar usuário já com papel definido
 *   2. promover/rebaixar entre admin, gestor e usuário
 *   3. escolher quais domínios cada gestor enxerga
 *   4. ativar/desativar quem está pendente
 *
 * O salvamento dos domínios manda o ESTADO FINAL numa requisição só
 * (PUT /users/{id}/domains), em vez de uma chamada por item — se falhar,
 * falha inteiro, e não pela metade.
 *
 * Isto é conveniência de admin: quem barra de verdade é a API. O papel vem do
 * localStorage e qualquer um edita no DevTools.
 */

const ROTULO_PAPEL: Record<string, string> = {
    admin: 'Administrador',
    manager: 'Gestor',
    user: 'Usuário',
};

const COR_PAPEL: Record<string, string> = {
    admin: 'bg-purple-50 text-purple-700 border-purple-200',
    manager: 'bg-blue-50 text-blue-700 border-blue-200',
    user: 'bg-gray-100 text-gray-600 border-gray-200',
};

export default function DomainAccess() {
    const currentUser = getCurrentUser();
    const isAdmin = currentUser?.role === 'admin';

    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [buscaUsuario, setBuscaUsuario] = useState('');
    const [buscaDominio, setBuscaDominio] = useState('');
    const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
    const [temMudanca, setTemMudanca] = useState(false);
    const [modalAberto, setModalAberto] = useState(false);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: usuarios = [], isLoading: carregandoUsuarios } = useQuery({
        queryKey: ['users'],
        queryFn: getUsers,
        enabled: isAdmin,
    });

    const { data: dominios = [], isLoading: carregandoDominios } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
        enabled: isAdmin,
    });

    const { data: acesso, isLoading: carregandoAcesso } = useQuery({
        queryKey: ['domain-access', selectedUserId],
        queryFn: () => getUserDomainAccess(selectedUserId!),
        enabled: !!selectedUserId,
    });

    useEffect(() => {
        if (!selectedUserId || !acesso) return;
        setSelecionados(new Set(acesso.domain_ids));
        setTemMudanca(false);
    }, [selectedUserId, acesso]);

    const usuarioSelecionado = usuarios.find((u: User) => u.id === selectedUserId);

    const salvarDominios = useMutation({
        mutationFn: () => setUserDomainAccess(selectedUserId!, [...selecionados]),
        onSuccess: (res) => {
            toast({
                title: 'Acessos salvos',
                description: res.total === 0
                    ? 'Este gestor não enxerga nenhum domínio.'
                    : `${res.total} domínio(s) liberado(s).`,
            });
            queryClient.invalidateQueries({ queryKey: ['domain-access', selectedUserId] });
            queryClient.invalidateQueries({ queryKey: ['users'] });
            setTemMudanca(false);
        },
        onError: (e: any) => toast({
            variant: 'destructive',
            title: 'Erro ao salvar',
            description: e.response?.data?.detail || 'Falha ao atualizar acessos.',
        }),
    });

    const trocarPapel = useMutation({
        mutationFn: ({ userId, papel }: { userId: number; papel: Papel }) =>
            setUserRole(userId, papel),
        onSuccess: (_r, v) => {
            toast({
                title: 'Papel alterado',
                description: `Agora é ${ROTULO_PAPEL[v.papel]}.`,
            });
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['domain-access', v.userId] });
        },
        onError: (e: any) => toast({
            variant: 'destructive',
            title: 'Erro ao alterar papel',
            description: e.response?.data?.detail || 'Falha ao alterar o papel.',
        }),
    });

    const alternarAtivo = useMutation({
        mutationFn: ({ userId, ativar }: { userId: number; ativar: boolean }) =>
            ativar ? aprovarUsuario(userId) : desativarUsuario(userId),
        onSuccess: (_r, v) => {
            toast({ title: v.ativar ? 'Usuário ativado' : 'Usuário desativado' });
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
        onError: (e: any) => toast({
            variant: 'destructive',
            title: 'Erro',
            description: e.response?.data?.detail || 'Falha ao alterar o status.',
        }),
    });

    const usuariosFiltrados = useMemo(
        () => usuarios.filter((u: User) =>
            u.name?.toLowerCase().includes(buscaUsuario.toLowerCase()) ||
            u.email?.toLowerCase().includes(buscaUsuario.toLowerCase()),
        ),
        [usuarios, buscaUsuario],
    );

    const dominiosFiltrados = useMemo(
        () => dominios.filter((d: Domain) =>
            d.domain?.toLowerCase().includes(buscaDominio.toLowerCase()),
        ),
        [dominios, buscaDominio],
    );

    const alternarDominio = (id: number) => {
        setSelecionados((anterior) => {
            const proximo = new Set(anterior);
            proximo.has(id) ? proximo.delete(id) : proximo.add(id);
            return proximo;
        });
        setTemMudanca(true);
    };

    const alternarTodosVisiveis = () => {
        setSelecionados((anterior) => {
            const proximo = new Set(anterior);
            const todosLigados = dominiosFiltrados.every((d: Domain) => proximo.has(d.id));
            dominiosFiltrados.forEach((d: Domain) =>
                todosLigados ? proximo.delete(d.id) : proximo.add(d.id));
            return proximo;
        });
        setTemMudanca(true);
    };

    if (!isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center p-20 text-gray-400">
                <Shield className="h-16 w-16 mb-4" />
                <h2 className="text-xl font-bold text-gray-600">Acesso Restrito</h2>
                <p className="mt-2">Apenas administradores podem gerenciar usuários e acessos.</p>
            </div>
        );
    }

    const ehAdminSelecionado = usuarioSelecionado?.role === 'admin';
    const ehEuMesmo = usuarioSelecionado?.id === currentUser?.id;

    return (
        <div className="space-y-6 pb-10">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 font-outfit tracking-tight">
                        Usuários e Acessos
                    </h1>
                    <p className="mt-1 text-gray-500 text-lg">
                        Crie usuários, defina o nível de acesso e escolha os sites de cada gestor.
                    </p>
                </div>
                <Button
                    onClick={() => setModalAberto(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg shadow-blue-200 gap-2"
                >
                    <UserPlus className="h-4 w-4" />
                    Novo usuário
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[400px,1fr] gap-6 items-start">
                {/* ------- coluna esquerda: usuários ------- */}
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-50">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar usuário..."
                                value={buscaUsuario}
                                onChange={(e) => setBuscaUsuario(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-200"
                            />
                        </div>
                    </div>

                    <div className="max-h-[560px] overflow-y-auto">
                        {carregandoUsuarios ? (
                            <div className="flex justify-center p-10">
                                <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
                            </div>
                        ) : usuariosFiltrados.length === 0 ? (
                            <div className="p-10 text-center text-gray-400 text-sm">Nenhum usuário</div>
                        ) : (
                            usuariosFiltrados.map((u: User) => (
                                <button
                                    key={u.id}
                                    onClick={() => setSelectedUserId(u.id)}
                                    className={cn(
                                        'w-full flex items-center gap-3 px-5 py-3.5 border-b border-gray-50 text-left transition-colors',
                                        selectedUserId === u.id ? 'bg-blue-50' : 'hover:bg-gray-50/70',
                                    )}
                                >
                                    <div className={cn(
                                        'h-9 w-9 rounded-full flex items-center justify-center shrink-0',
                                        u.is_active ? 'bg-blue-100' : 'bg-gray-100',
                                    )}>
                                        <span className={cn(
                                            'text-sm font-bold',
                                            u.is_active ? 'text-blue-600' : 'text-gray-400',
                                        )}>
                                            {u.name?.charAt(0)?.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium text-gray-900 text-sm truncate">{u.name}</p>
                                            {!u.is_active && (
                                                <Badge variant="secondary" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 shrink-0">
                                                    pendente
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-400 truncate">{u.email}</p>
                                    </div>
                                    <Badge
                                        variant="secondary"
                                        className={cn('text-[10px] shrink-0 border', COR_PAPEL[u.role ?? 'user'])}
                                    >
                                        {ROTULO_PAPEL[u.role ?? 'user']}
                                    </Badge>
                                    <ChevronRight className={cn(
                                        'h-4 w-4 shrink-0',
                                        selectedUserId === u.id ? 'text-blue-500' : 'text-gray-300',
                                    )} />
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* ------- coluna direita: detalhe ------- */}
                {!selectedUserId ? (
                    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm flex flex-col items-center justify-center py-24 text-gray-400">
                        <Shield className="h-14 w-14 mb-4 text-gray-200" />
                        <h3 className="text-lg font-semibold text-gray-500">Selecione um usuário</h3>
                        <p className="text-sm mt-1">Escolha alguém na lista para definir acesso e domínios.</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* nível de acesso */}
                        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                                        Nível de acesso
                                    </h2>
                                    <p className="mt-1 font-bold text-gray-900 text-lg">{usuarioSelecionado?.name}</p>
                                    <p className="text-sm text-gray-400">{usuarioSelecionado?.email}</p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className={cn(
                                        'rounded-lg text-xs gap-1.5 shrink-0',
                                        usuarioSelecionado?.is_active
                                            ? 'text-red-600 border-red-200 hover:bg-red-50'
                                            : 'text-green-600 border-green-200 hover:bg-green-50',
                                    )}
                                    disabled={ehEuMesmo || alternarAtivo.isPending}
                                    onClick={() => alternarAtivo.mutate({
                                        userId: selectedUserId,
                                        ativar: !usuarioSelecionado?.is_active,
                                    })}
                                >
                                    {usuarioSelecionado?.is_active
                                        ? <><Ban className="h-3.5 w-3.5" /> Desativar</>
                                        : <><Check className="h-3.5 w-3.5" /> Ativar</>}
                                </Button>
                            </div>

                            <div className="mt-5 grid grid-cols-3 gap-2">
                                {(['user', 'manager', 'admin'] as Papel[]).map((p) => {
                                    const ativo = usuarioSelecionado?.role === p;
                                    return (
                                        <button
                                            key={p}
                                            disabled={ehEuMesmo || trocarPapel.isPending}
                                            onClick={() => trocarPapel.mutate({ userId: selectedUserId, papel: p })}
                                            className={cn(
                                                'px-4 py-3 rounded-xl border text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                                                ativo
                                                    ? 'border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                                                    : 'border-gray-100 bg-gray-50 text-gray-600 hover:bg-white hover:border-gray-200',
                                            )}
                                        >
                                            {ROTULO_PAPEL[p]}
                                        </button>
                                    );
                                })}
                            </div>

                            {ehEuMesmo && (
                                <p className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                                    Você não pode alterar o próprio papel nem se desativar — evita ficar
                                    sem nenhum admin no sistema.
                                </p>
                            )}
                            {!ehEuMesmo && usuarioSelecionado?.role === 'user' && (
                                <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                    Como <strong>Usuário</strong>, hoje ele enxerga <strong>todos</strong> os
                                    domínios. Marque domínios abaixo e salve: ele vira Gestor automaticamente.
                                </p>
                            )}
                        </div>

                        {/* domínios */}
                        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3 min-w-0">
                                    <Globe className="h-5 w-5 text-blue-500 shrink-0" />
                                    <h2 className="font-semibold text-gray-900 truncate">
                                        Domínios
                                        <span className="ml-2 text-sm font-normal text-gray-400">
                                            {ehAdminSelecionado
                                                ? 'admin enxerga todos'
                                                : `${selecionados.size} de ${dominios.length} liberados`}
                                        </span>
                                    </h2>
                                </div>
                                {!ehAdminSelecionado && (
                                    <div className="flex items-center gap-2 shrink-0">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Buscar domínio..."
                                                value={buscaDominio}
                                                onChange={(e) => setBuscaDominio(e.target.value)}
                                                className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-200 w-52"
                                            />
                                        </div>
                                        <Button
                                            variant="outline" size="sm"
                                            className="rounded-lg text-xs"
                                            onClick={alternarTodosVisiveis}
                                        >
                                            Alternar todos
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {ehAdminSelecionado ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                                    <Shield className="h-10 w-10 mb-3 text-purple-200" />
                                    <p className="text-sm text-gray-500">
                                        Administrador enxerga todos os {dominios.length} domínios.
                                    </p>
                                    <p className="text-xs mt-1">
                                        Para restringir, mude o nível de acesso para Gestor.
                                    </p>
                                </div>
                            ) : carregandoDominios || carregandoAcesso ? (
                                <div className="flex justify-center p-16">
                                    <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                                </div>
                            ) : dominiosFiltrados.length === 0 ? (
                                <div className="flex flex-col items-center justify-center p-16 text-gray-400">
                                    <AlertCircle className="h-8 w-8 mb-2" />
                                    <p className="text-sm">Nenhum domínio encontrado</p>
                                </div>
                            ) : (
                                <div className="max-h-[520px] overflow-y-auto">
                                    {dominiosFiltrados.map((d: Domain) => {
                                        const ligado = selecionados.has(d.id);
                                        return (
                                            <div
                                                key={d.id}
                                                className={cn(
                                                    'grid grid-cols-[1fr,90px] px-6 py-3 border-b border-gray-50 items-center hover:bg-gray-50/50 transition-colors',
                                                    ligado && 'bg-blue-50/30',
                                                )}
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="h-9 w-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 text-sm font-bold shrink-0">
                                                        {d.domain?.charAt(0)?.toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-gray-900 text-sm truncate">{d.domain}</p>
                                                        <p className="text-xs text-gray-400">ID: {d.id}</p>
                                                    </div>
                                                    {!d.is_active && (
                                                        <Badge variant="secondary" className="text-[9px] bg-red-50 text-red-500 border-red-100 shrink-0">
                                                            inativo
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="flex justify-center">
                                                    <Switch
                                                        checked={ligado}
                                                        onCheckedChange={() => alternarDominio(d.id)}
                                                        className="data-[state=checked]:bg-[#111827]"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {temMudanca && selectedUserId && !ehAdminSelecionado && (
                <div className="fixed bottom-6 right-6 z-50">
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl shadow-2xl px-4 py-3">
                        <span className="text-sm text-gray-500">Alterações não salvas</span>
                        <Button
                            variant="ghost" size="sm" className="rounded-lg"
                            onClick={() => {
                                setSelecionados(new Set(acesso?.domain_ids ?? []));
                                setTemMudanca(false);
                            }}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={() => salvarDominios.mutate()}
                            disabled={salvarDominios.isPending}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold gap-2"
                        >
                            {salvarDominios.isPending
                                ? <RefreshCw className="h-4 w-4 animate-spin" />
                                : <Save className="h-4 w-4" />}
                            Salvar
                        </Button>
                    </div>
                </div>
            )}

            <NovoUsuarioModal
                aberto={modalAberto}
                aoFechar={() => setModalAberto(false)}
                aoCriar={(userId) => setSelectedUserId(userId)}
            />
        </div>
    );
}
